import React from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  InputRightElement,
  InputLeftElement,
  InputGroup,
} from '@chakra-ui/react';
import { UseFormRegisterReturn, FieldError } from 'react-hook-form';

interface AleaInputProps {
  label: string;
  placeholder?: string;
  value: string;
  type?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  register: UseFormRegisterReturn;
  error?: FieldError | string;
  disabled?: boolean;
  suffix?: string;
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
  sx?: Record<string, any>;
  labelFontWeight?: number;
  isRequired?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  autoComplete?: string;
}

const AleaInput: React.FC<AleaInputProps> = ({
  label,
  placeholder,
  value,
  type = 'text', // Default type is 'text'
  onChange,
  register,
  error,
  disabled,
  suffix,
  leftIcon,
  rightIcon,
  sx = {},
  labelFontWeight,
  isRequired,
  onKeyDown,
  autoComplete,
}) => {
  const formLabelStyles = {
    fontWeight: labelFontWeight ?? (isRequired ? 500 : 300),
    marginBottom: '3px',
    color: 'alea.500',
    paddingLeft: '16px',
  };

  const inputStyles = {
    height: '50px',
    borderRadius: '20px',
    border: '1px solid',
    borderColor: '#DCDCDC',
    color: '#000000',
    fontSize: '18px',
    _focus: { borderColor: 'gray.500' },
    _focusVisible: { borderColor: 'alea.500' },
    _invalid: { borderColor: 'invalid.500' },
    paddingLeft: leftIcon ? '45px' : undefined,
    paddingRight: (rightIcon || suffix) ? '50px' : undefined,
    ...sx,
  };

  const errorMessage = typeof error === 'string' ? error : error?.message;

  return (
    <FormControl isInvalid={!!error}>
      {label && <FormLabel sx={formLabelStyles}>{label}</FormLabel>}
      <InputGroup>
        {leftIcon && (
          <InputLeftElement
            h="50px"
            width="55px"
            pointerEvents="none"
          >
            {leftIcon}
          </InputLeftElement>
        )}
        <Input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            register.onChange(e); // Call react-hook-form's onChange
            onChange(e); // Call the passed in onChange handler
          }}
          name={register.name}
          ref={register.ref}
          onBlur={register.onBlur}
          onKeyDown={onKeyDown}
          sx={inputStyles}
          disabled={disabled}
          bg="white"
          autoComplete={autoComplete}
        />
        {(suffix || rightIcon) && (
          <InputRightElement
            h="50px"
            width="55px"
            color="alea.500"
            fontSize="20px"
            fontWeight={400}
            pointerEvents={rightIcon ? "none" : "auto"}
          >
            {suffix || rightIcon}
          </InputRightElement>
        )}
      </InputGroup>
      {errorMessage && (
        <FormErrorMessage
          pl="16px"
          mt="3px"
          color="invalid.500"
          fontSize="15px"
          fontWeight={300}
        >
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default AleaInput;
